package com.evoapps.evoshift.domain.usecase.hiring

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.ApplicantStatus
import com.evoapps.evoshift.domain.model.CvRequirement
import com.evoapps.evoshift.domain.model.CvSettings
import com.evoapps.evoshift.domain.model.RejectionMailTemplate
import com.evoapps.evoshift.domain.model.InvalidJobPostException
import com.evoapps.evoshift.domain.model.InvalidJobPostException.Problem
import com.evoapps.evoshift.domain.model.JobApplication
import com.evoapps.evoshift.domain.model.JobPost
import com.evoapps.evoshift.domain.model.JobPostStatus
import com.evoapps.evoshift.domain.repository.HiringRepository
import kotlinx.datetime.LocalDate
import kotlin.time.Clock

/**
 * A manager saves a job post (SH-125): trimmed texts within the limits of firestore.rules, a
 * title, the employer's name for the privacy notice and a pay range from above zero (required by
 * the pay transparency rules, art. 18³ᶜᵃ KP), and a valid last day. Stamps who and when.
 */
class SaveJobPostUseCase(
    private val repository: HiringRepository,
    private val clock: Clock = AppClock
) {
    suspend operator fun invoke(post: JobPost, editorId: String): Result<JobPost> = runCatching {
        val clean = validated(post)
        val now = clock.now().toString()
        val isNew = post.id.isBlank() || post.createdAt.isBlank()
        val stamped = clean.copy(
            createdAt = if (isNew) now else clean.createdAt,
            createdBy = if (isNew) editorId else clean.createdBy,
            updatedAt = now,
            updatedBy = editorId
        )
        val id = repository.savePost(stamped, isNew).getOrThrow()
        stamped.copy(id = id)
    }

    companion object {
        /** [post] trimmed, or why it cannot be saved. */
        fun validated(post: JobPost): JobPost {
            val title = post.title.trim()
            if (title.isEmpty()) throw InvalidJobPostException(Problem.TITLE_REQUIRED)
            // In the order of the editor's fields.
            if (post.payMin <= 0.0 || post.payMax <= 0.0) throw InvalidJobPostException(Problem.PAY_REQUIRED)
            if (post.payMax < post.payMin || post.payMax > JobPost.MAX_PAY) throw InvalidJobPostException(Problem.PAY_RANGE)
            val employer = post.employerName.trim()
            if (employer.isEmpty()) throw InvalidJobPostException(Problem.EMPLOYER_REQUIRED)
            // The privacy notice must say how to reach the controller (Art. 13(1)(a) GDPR).
            if (post.privacyContact.isBlank()) throw InvalidJobPostException(Problem.CONTACT_REQUIRED)
            val closesOn = post.closesOn?.trim()?.takeIf { it.isNotEmpty() }
            if (closesOn != null && runCatching { LocalDate.parse(closesOn) }.isFailure) {
                throw InvalidJobPostException(Problem.DATE_INVALID)
            }
            // SH-210: a CV asked for needs at least one kind of file; sizes stay small.
            if (post.cv.requirement != CvRequirement.NONE && post.cv.types.isEmpty()) {
                throw InvalidJobPostException(Problem.CV_TYPES_REQUIRED)
            }
            val cv = post.cv.copy(
                maxMb = post.cv.maxMb.coerceIn(CvSettings.MIN_MB, CvSettings.MAX_MB),
                types = post.cv.types.ifEmpty { CvSettings().types }
            )
            val mail = post.rejectionMail.copy(subject = post.rejectionMail.subject.trim(), body = post.rejectionMail.body.trim())
            val clean = post.copy(
                cv = cv,
                rejectionMail = mail,
                recruiterIds = post.recruiterIds.filter { it.isNotBlank() }.distinct(),
                title = title,
                role = post.role.trim(),
                description = post.description.trim(),
                hoursText = post.hoursText.trim(),
                city = post.city.trim(),
                employerName = employer,
                privacyContact = post.privacyContact.trim(),
                currency = post.currency.trim().uppercase().ifEmpty { "PLN" },
                closesOn = closesOn,
                lang = if (post.lang == "en" || post.lang == "uk") post.lang else "pl"
            )
            val tooLong = clean.title.length > JobPost.MAX_TITLE || clean.role.length > JobPost.MAX_ROLE ||
                clean.description.length > JobPost.MAX_DESCRIPTION || clean.hoursText.length > JobPost.MAX_HOURS ||
                clean.city.length > JobPost.MAX_CITY || clean.employerName.length > JobPost.MAX_EMPLOYER ||
                clean.privacyContact.length > JobPost.MAX_CONTACT || clean.currency.length != 3 ||
                clean.recruiterIds.size > JobPost.MAX_RECRUITERS ||
                clean.rejectionMail.subject.length > RejectionMailTemplate.MAX_SUBJECT ||
                clean.rejectionMail.body.length > RejectionMailTemplate.MAX_BODY
            if (tooLong) throw InvalidJobPostException(Problem.TOO_LONG)
            return clean
        }
    }
}

/** Opens, closes or takes back a post to a draft (the public page follows at once). */
class SetJobPostStatusUseCase(
    private val repository: HiringRepository,
    private val save: SaveJobPostUseCase
) {
    suspend operator fun invoke(postId: String, status: JobPostStatus, editorId: String): Result<JobPost> = runCatching {
        val post = repository.getPost(postId).getOrThrow()
        if (post.status == status) return@runCatching post
        save(post.copy(status = status), editorId).getOrThrow()
    }
}

/**
 * Moves an applicant to another stage with the manager's note (trimmed, up to
 * [JobApplication.MAX_MANAGER_NOTE]; blank: none). The server stamps the decision and the
 * retention that follows from it.
 */
class SetApplicantStatusUseCase(
    private val repository: HiringRepository,
    private val clock: Clock = AppClock
) {
    suspend operator fun invoke(
        application: JobApplication,
        status: ApplicantStatus,
        managerNote: String?,
        editorId: String
    ): Result<JobApplication> = runCatching {
        val note = managerNote?.trim()?.takeIf { it.isNotEmpty() }?.take(JobApplication.MAX_MANAGER_NOTE)
        repository.updateApplication(application.postId, application.id, status, note, editorId, clock.now().toString()).getOrThrow()
        application.copy(status = status, managerNote = note)
    }
}

/**
 * "Hire": the applicant became a team member through the employee form and its invitation; their
 * application is marked HIRED (deleted by the server 30 days later).
 */
class MarkApplicantHiredUseCase(
    private val repository: HiringRepository,
    private val setStatus: SetApplicantStatusUseCase
) {
    suspend operator fun invoke(postId: String, applicationId: String, editorId: String): Result<Unit> = runCatching {
        val application = repository.getApplication(postId, applicationId).getOrThrow()
        if (application.status != ApplicantStatus.HIRED) {
            setStatus(application, ApplicantStatus.HIRED, application.managerNote, editorId).getOrThrow()
        }
    }
}
